import { useContext, useEffect, useState } from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/joy';
import { useNavigate } from 'react-router';
import { Fire } from '@phosphor-icons/react';
import { LayoutContext } from '../../context';
import { APIGetRoadmaps, APIGetHomepageData, APIGetFreezeStatus, APIActivateInvoice } from '../../api';
import { buildAccessMap } from './access';
import TodayCard from './TodayCard';
import AccessMap from './AccessMap';
import MiniStats from './MiniStats';
import PlatformsCard from './PlatformsCard';
import DailyQuestionCard from './DailyQuestionCard';
import WeeklyActivity from './WeeklyActivity';
import FreeAccessCard from './FreeAccessCard';
import SubscriptionCard from './SubscriptionCard';
import FreezeModal from '../../components/modals/FreezeModal';
import TelegramInviteBanner from '../../components/ui/TelegramInviteBanner';

/**
 * Главная v2: карта доступа (спека 2026-09-06-home-v2-access-map-design.md).
 * Включается по ?home=v2 или localStorage.apex_home === 'v2', см. HomeSwitch в App.js.
 */
export default function HomeV2() {
    const { token, user, trialInfo, openUpgradeModal, loadTrialStatus } = useContext(LayoutContext);
    const navigate = useNavigate();
    const [roadmap, setRoadmap] = useState(undefined);
    const [stats, setStats] = useState(null);
    const [freeze, setFreeze] = useState(null);
    const [freezeOpen, setFreezeOpen] = useState(false);
    const [activating, setActivating] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await APIGetRoadmaps(token);
                if (alive) setRoadmap(res?.success && res.roadmaps?.length ? res.roadmaps[0] : null);
            } catch (e) {
                console.error('HomeV2: roadmaps', e);
                if (alive) setRoadmap(null);
            }
        })();
        loadStats(() => alive);
        loadFreeze(() => alive);
        return () => { alive = false; };
    }, [token]);   // eslint-disable-line react-hooks/exhaustive-deps

    const loadStats = async (alive = () => true) => {
        try {
            const res = await APIGetHomepageData(token);
            if (alive() && res?.success) setStats(res.data);
        } catch (e) { console.error('HomeV2: homepage-data', e); }
    };
    const loadFreeze = async (alive = () => true) => {
        try {
            const res = await APIGetFreezeStatus(token);
            if (alive() && res?.success) setFreeze(res);
        } catch (e) { /* кнопка заморозки просто не появится */ }
    };

    // Оплачено → «Активировать тариф»: срок стартует сейчас, доступ открывается
    const handleActivate = async (uuidPay) => {
        if (!uuidPay || activating) return;
        setActivating(true);
        try {
            const res = await APIActivateInvoice(token, uuidPay);
            if (res?.success) {
                await Promise.all([loadStats(), loadFreeze(), loadTrialStatus?.()]);
                try { const rm = await APIGetRoadmaps(token); if (rm?.success) setRoadmap(rm.roadmaps?.[0] || null); } catch { /* ignore */ }
            } else {
                alert(res?.error || 'Не удалось активировать');
            }
        } catch (e) { alert('Ошибка активации'); }
        setActivating(false);
    };

    if (roadmap === undefined) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size="sm" /></Box>;
    }

    const map = buildAccessMap({ roadmap, trialInfo, user });
    const goToPlan = (d) => navigate(`/plans?roadmapId=${d.roadmapId}&rmId=${d.moduleId ?? d.id}`);
    const onLocked = (item) => openUpgradeModal?.(
        item?.title ? `«${item.title}» откроется в APEX PRO.` : item?.name ? `«${item.name}» входит в APEX PRO.` : 'Откройте всё в APEX PRO.'
    );

    // Карточка рядом с «Сегодня»: тариф / активация / бесплатный доступ
    const hasTariff = !!(stats?.subscription || stats?.pending_activation);
    const sideCard = hasTariff
        ? <SubscriptionCard subscription={stats.subscription} pendingActivation={stats.pending_activation} freeze={freeze}
            activating={activating} onActivate={handleActivate}
            onRenew={() => openUpgradeModal?.('Продлите доступ, чтобы не потерять прогресс.')}
            onFreeze={() => setFreezeOpen(true)} />
        : !map.full
            ? <FreeAccessCard trialDaysLeft={map.trialDaysLeft} onChoose={() => onLocked(null)} />
            : null;

    const streak = user?.streak_current;
    const level = user?.xp_level;

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 800, letterSpacing: '-0.02em' }}>
                    Привет{user?.first_name ? `, ${user.first_name}` : ''}
                </Typography>
                {(streak > 0 || level > 0) && (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {streak > 0 && <><Fire size={14} weight="fill" color="#DA7756" /> {streak} дн. подряд</>}
                        {streak > 0 && level > 0 && ' · '}
                        {level > 0 && `уровень ${level}`}
                    </Typography>
                )}
            </Stack>

            <Stack spacing={2}>
                <PlatformsCard />
                <TelegramInviteBanner subscription={stats?.subscription} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: sideCard ? '1fr 1fr' : '1fr' }, gap: 2, alignItems: 'stretch' }}>
                    <TodayCard
                        today={map.today}
                        onGo={() => map.today.kind !== 'none' && goToPlan(map.today)}
                    />
                    {sideCard}
                </Box>
                {stats && <MiniStats stats={stats} onDetails={() => navigate('/stats')} />}
                <AccessMap map={map} onOpenDiscipline={goToPlan} onLocked={onLocked} onNavigate={(to) => navigate(to)} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2, alignItems: 'start' }}>
                    <DailyQuestionCard token={token} />
                    <WeeklyActivity weekly={stats?.weekly_activity} />
                </Box>
            </Stack>
            <FreezeModal open={freezeOpen} onClose={() => setFreezeOpen(false)} token={token} expiresAt={freeze?.expires_at} onFrozen={() => { setFreezeOpen(false); loadFreeze(); loadStats(); }} />
        </Box>
    );
}
